import { isEmpty } from 'lodash/fp';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NoItemResult from '../../../components/results/NoItemResult';
import GroupedBarChart from './Charts/GroupedBarChart';
import { useDashboardContext } from './DashboardContext';
import usePopularEventLeadsData from './data/usePopularEventLeadsData';
import { FullScreenWrapper } from './shared/FullScreenWrapper';
import { type ChartDataProps, type Datum } from './shared/typings';
import useLegendSelectedConfig from './shared/useLegendSelectedConfig';
import useSeriesColors from './shared/useSeriesColors';
import useSeriesFields from './shared/useSeriesField';

export const PopularLeadEventViewID = 'popularLeadEventChart';

const PopularLeadEventChartContent = ({ data, t, noData }: ChartDataProps) => {
    const { currentMonth, lastMonth } = useSeriesFields();
    const legendSelected = useLegendSelectedConfig({ currentMonth, lastMonth });
    const color = useSeriesColors();

    if (noData) {
        return <NoItemResult subTitle={t('dashboard:noData.message')} />;
    }

    return (
        <GroupedBarChart
            {...t('dashboard:common.chart', { returnObjects: true })}
            color={color}
            data={data}
            visible={legendSelected}
            xField="noOfSubmitted"
            yField="event"
        />
    );
};

const PopularLeadEventChart = () => {
    const { t } = useTranslation(['dashboard']);
    const { isLoading, error, generateData } = usePopularEventLeadsData();
    const [data, setData] = useState<Datum | null>(null);

    useEffect(() => {
        generateData(result => setData(result));
    }, [generateData, setData]);

    const noData = useMemo(() => (data ? Object.values(data).every(isEmpty) : true), [data]);

    const { setVisible } = useDashboardContext();

    useEffect(() => {
        setVisible({ [PopularLeadEventViewID]: !error });
    }, [error, setVisible]);

    if (error) {
        return null;
    }

    return (
        <FullScreenWrapper
            disabledFullScreen={noData}
            isLoading={isLoading}
            title={t('dashboard:popularLeadEventChart.title')}
        >
            <PopularLeadEventChartContent data={data} noData={noData} t={t} />
        </FullScreenWrapper>
    );
};

export default PopularLeadEventChart;
